/**
 * Localization strings that are shared across components
 *
 * @module
 */

import { createDictionary } from './utils';

// Refer to "Guidelines for Programmers" in ./README.md before editing this file

export const commonText = createDictionary({
  specifySeven: {
    comment: `
      This is an example of how to provide comments. Comments are visible to
      translators.
    `,
    'en-us': 'Specify 7',
    'ru-ru': 'Укажите 7',
    'es-es': 'Especificar 7',
    'fr-fr': 'Specify 7',
    'uk-ua': 'Вкажіть 7',
    'de-ch': 'Specify 7',
    'pt-br': 'Especifique 7',
    'hr-hr': 'Navedite 7',
    nb: 'Specify 7',
  },
  no: {
    'en-us': 'No',
    'ru-ru': 'Нет',
    'es-es': 'No',
    'fr-fr': 'Non',
    'uk-ua': 'Ні',
    'de-ch': 'Nein',
    'pt-br': 'Não',
    'hr-hr': 'Ne',
    nb: 'Nei',
  },
  cancel: {
    'en-us': 'Cancel',
    'ru-ru': 'Отмена',
    'es-es': 'Cancelar',
    'fr-fr': 'Annuler',
    'uk-ua': 'Скасувати',
    'de-ch': 'Abbrechen',
    'pt-br': 'Cancelar',
    'hr-hr': 'Otkazati',
    nb: 'Avbryt',
  },
  back: {
    'en-us': 'Back',
    'ru-ru': 'Назад',
    'es-es': 'Atrás',
    'fr-fr': 'Retour arrière',
    'uk-ua': 'Назад',
    'de-ch': 'Zurück',
    'pt-br': 'Voltar',
    'hr-hr': 'Nazad',
    nb: 'Tilbake',
  },
  skip: {
    'en-us': 'Skip',
    'ru-ru': 'Пропустить',
    'es-es': 'Saltar',
    'fr-fr': 'Sauter',
    'uk-ua': 'Пропустити',
    'de-ch': 'Überspringen',
    'pt-br': 'Pular',
    'hr-hr': 'Preskočiti',
    nb: 'Hopp over',
  },
  create: {
    'en-us': 'Create',
    'ru-ru': 'Создавать',
    'es-es': 'Crear',
    'fr-fr': 'Créer',
    'uk-ua': 'Створити',
    'de-ch': 'Erstellen',
    'pt-br': 'Criar',
    'hr-hr': 'Stvoriti',
    nb: 'Skape',
  },
  close: {
    'en-us': 'Close',
    'ru-ru': 'Закрывать',
    'es-es': 'Cerca',
    'fr-fr': 'Fermer',
    'uk-ua': 'Закрити',
    'de-ch': 'Schliessen',
    'pt-br': 'Fechar',
    'hr-hr': 'Zatvoriti',
    nb: 'Lukke',
  },
  configureExpressSearch: {
    'en-us': 'Configure Express Search',
    'ru-ru': 'Настройка экспресс-поиска',
    'es-es': 'Configurar la búsqueda rápida',
    'fr-fr': 'Configurer Express Search',
    'uk-ua': 'Налаштування експрес-пошуку',
    'de-ch': 'Express Search konfigurieren',
    'pt-br': 'Configurar pesquisa expressa',
    'hr-hr': 'Konfigurirajte Express Search',
    nb: 'Konfigurer ekspresssøk',
  },
  expressSearchInstructionsTitle: {
    'en-us': 'Search tips',
    'ru-ru': 'Советы по поиску',
    'es-es': 'Consejos de búsqueda',
    'fr-fr': 'Conseils de recherche',
    'uk-ua': 'Поради щодо пошуку',
    'de-ch': 'Suchtipps',
    'pt-br': 'Dicas de pesquisa',
    'hr-hr': 'Savjeti za pretraživanje',
    nb: 'Søketips',
  },
  expressSearchInstructions: {
    'en-us':
      'Separate multiple search terms with spaces, use % anywhere, * at the beginning or end, and wrap terms in quotes for exact multi-word matches.',
    'de-ch':
      'Mehrere Suchbegriffe sollten durch Leerzeichen getrennt werden, % kann an beliebiger Stelle verwendet werden, * kann am Anfang oder Ende stehen, und für exakte Übereinstimmungen aus mehreren Wörtern sollten die Begriffe in Anführungszeichen gesetzt werden.',
    'es-es':
      'Separe los términos de búsqueda con espacios, use % en cualquier parte, * al principio o al final, y ponga los términos entre comillas para coincidencias exactas de varias palabras.',
    'fr-fr':
      "Séparez les termes de recherche multiples par des espaces, utilisez % n'importe où, * au début ou à la fin pour remplacer un ou plusieurs caractères, et placez les termes entre guillemets pour obtenir des correspondances exactes avec plusieurs mots.",
    'hr-hr':
      'Odvojite više pojmova za pretraživanje razmacima, koristite % bilo gdje, * na početku ili kraju i stavite pojmove u navodnike za točna podudaranja više riječi.',
    nb: 'Skill flere søkeord med mellomrom, bruk % hvor som helst, * i begynnelsen eller slutten, og bryt søkeord i anførselstegn for eksakte treff med flere ord.',
    'pt-br':
      'Separe vários termos de pesquisa com espaços, use % em qualquer lugar, * no início ou no fim e coloque os termos entre aspas para correspondências exatas de várias palavras.',
    'ru-ru':
      'Разделяйте несколько поисковых запросов пробелами, используйте % в любом месте, * в начале или конце, а для точного совпадения нескольких слов заключайте запросы в кавычки.',
    'uk-ua':
      'Розділяйте кілька пошукових термінів пробілами, використовуйте % будь-де, * на початку або в кінці та беріть терміни в лапки для точного збігу кількох слів.',
  },
  expressSearchDateFormats: {
    'en-us':
      'Dates can be searched using either the YYYY-MM-DD or MM/DD/YYYY format.',
    'de-ch':
      'Die Datumssuche kann entweder im Format JJJJ-MM-TT oder im Format MM/TT/JJJJ erfolgen.',
    'es-es':
      'Las fechas se pueden buscar utilizando el formato AAAA-MM-DD o MM/DD/AAAA.',
    'fr-fr':
      'Les dates peuvent être recherchées au format AAAA-MM-JJ ou MM/JJ/AAAA.',
    'hr-hr': 'Datumi se mogu pretraživati u formatu GGGG-MM-DD ili MM/DD/GGGG.',
    nb: 'Datoer kan søkes etter enten i formatet ÅÅÅÅ-MM-DD eller MM/DD/ÅÅÅÅ.',
    'pt-br':
      'As datas podem ser pesquisadas usando o formato AAAA-MM-DD ou MM/DD/AAAA.',
    'ru-ru':
      'Поиск дат можно осуществлять в формате ГГГГ-ММ-ДД или ММ/ДД/ГГГГ.',
    'uk-ua': 'Дати можна шукати у форматі РРРР-ММ-ДД або ММ/ДД/РРРР.',
  },
  expressSearchPhraseExample: {
    'en-us':
      'To search a term with spaces, wrap the phrase in quotes, for example "Clinton Lake".',
    'de-ch':
      'Um nach einem Begriff mit Leerzeichen zu suchen, setzen Sie den Ausdruck in Anführungszeichen, zum Beispiel „Clinton Lake“.',
    'es-es':
      'Para buscar un término con espacios, encierre la frase entre comillas, por ejemplo "Clinton Lake".',
    'fr-fr':
      "Pour rechercher un terme contenant des espaces, placez l'expression entre guillemets, par exemple « Lac Léman ».",
    'hr-hr':
      'Za pretraživanje pojma s razmacima, stavite frazu u navodnike, na primjer "Clinton Lake".',
    nb: 'For å søke etter et ord med mellomrom, sett frasen i anførselstegn, for eksempel «Clinton Lake».',
    'pt-br':
      'Para pesquisar um termo com espaços, coloque a frase entre aspas, por exemplo, "Lago Clinton".',
    'ru-ru':
      'Для поиска термина, содержащего пробелы, заключите фразу в кавычки, например, "Clinton Lake".',
    'uk-ua':
      'Щоб знайти термін із пробілами, візьміть фразу в лапки, наприклад, «Клінтон-Лейк».',
  },
  apply: {
    'en-us': 'Apply',
    'ru-ru': 'Применять',
    'es-es': 'Aplicar',
    'fr-fr': 'Appliquer',
    'uk-ua': 'Застосувати',
    'de-ch': 'Anwenden',
    'pt-br': 'Aplicar',
    'hr-hr': 'Primijeni',
    nb: 'Søke',
  },
  applyAll: {
    'en-us': 'Apply All',
    'ru-ru': 'Применить все',
    'es-es': 'Aplicar todo',
    'fr-fr': 'Appliquer à toutes les lignes',
    'uk-ua': 'Застосувати все',
    'de-ch': 'Alle Anwenden',
    'pt-br': 'Aplicar tudo',
    'hr-hr': 'Primijeni sve',
    nb: 'Bruk alle',
  },
  clearAll: {
    'en-us': 'Clear all',
    'ru-ru': 'Очистить все',
    'es-es': 'Borrar todo',
    'fr-fr': 'Tout effacer',
    'uk-ua': 'Очистити все',
    'de-ch': 'Alles löschen',
    'pt-br': 'Limpar tudo',
    'hr-hr': 'Obriši sve',
    nb: 'Fjern alle',
  },
  deleteUnmapped: {
    'en-us': 'Delete unmapped columns',
    'de-ch': 'Nicht zugeordnete Spalten löschen',
    'es-es': 'Eliminar columnas no asignadas',
    'fr-fr': 'Supprimer les colonnes non mappées',
    'pt-br': 'Excluir colunas não mapeadas',
    'ru-ru': 'Удалить несопоставленные столбцы',
    'uk-ua': 'Видалити незіставлені стовпці',
    'hr-hr': 'Izbriši nemapirane stupce',
    nb: 'Slett ikke-tilordnede kolonner',
  },
  save: {
    'en-us': 'Save',
    'ru-ru': 'Сохранять',
    'es-es': 'Ahorrar',
    'fr-fr': 'Sauvegarder',
    'uk-ua': 'Зберегти',
    'de-ch': 'Speichern',
    'pt-br': 'Salvar',
    'hr-hr': 'Uštedjeti',
    nb: 'Spare',
  },
  add: {
    'en-us': 'Add',
    'ru-ru': 'Добавлять',
    'es-es': 'Agregar',
    'fr-fr': 'Ajouter',
    'uk-ua': 'Додати',
    'de-ch': 'Hinzufügen',
    'pt-br': 'Adicionar',
    'hr-hr': 'Dodati',
    nb: 'Legge til',
  },
  open: {
    'en-us': 'Open',
    'ru-ru': 'Открыть',
    'es-es': 'Abierto',
    'fr-fr': 'Ouvrir',
    'uk-ua': 'ВІДЧИНЕНО',
    'de-ch': 'Öffnen',
    'pt-br': 'Abrir',
    'hr-hr': 'Otvoriti',
    nb: 'Åpne',
  },
  delete: {
    'en-us': 'Delete',
    'es-es': 'Borrar',
    'fr-fr': 'Supprimer',
    'uk-ua': 'Видалити',
    'de-ch': 'Löschen',
    'ru-ru': 'Удалить',
    'pt-br': 'Excluir',
    'hr-hr': 'Izbrisati',
    nb: 'Slett',
  },
  next: {
    'en-us': 'Next',
    'ru-ru': 'Следующий',
    'es-es': 'Próximo',
    'fr-fr': 'Suivant',
    'uk-ua': 'Далі',
    'de-ch': 'Weiter',
    'pt-br': 'Próximo',
    'hr-hr': 'Sljedeći',
    nb: 'Neste',
  },
  previous: {
    'en-us': 'Previous',
    'ru-ru': 'Предыдущий',
    'es-es': 'Anterior',
    'fr-fr': 'Précédent',
    'uk-ua': 'Попередній',
    'de-ch': 'Zurück',
    'pt-br': 'Anterior',
    'hr-hr': 'Prethodno',
    nb: 'Tidligere',
  },
  tool: {
    'en-us': 'Tool',
    'ru-ru': 'Инструмент',
    'es-es': 'Herramienta',
    'fr-fr': 'Outil',
    'uk-ua': 'Інструмент',
    'de-ch': 'Tool',
    'pt-br': 'Ferramenta',
    'hr-hr': 'Alat',
    nb: 'Verktøy',
  },
  tools: {
    'en-us': 'Tools',
    'ru-ru': 'Инструменты',
    'es-es': 'Herramientas',
    'fr-fr': 'Outils',
    'uk-ua': 'Інструменти',
    'de-ch': 'Tools',
    'pt-br': 'Ferramentas',
    'hr-hr': 'Alati',
    nb: 'Verktøy',
  },
  loading: {
    'en-us': 'Loading…',
    'ru-ru': 'Загрузка…',
    'es-es': 'Cargando…',
    'fr-fr': 'Chargement…',
    'uk-ua': 'Завантаження…',
    'de-ch': 'Laden …',
    'pt-br': 'Carregando…',
    'hr-hr': 'Učitavanje…',
    nb: 'Laster inn…',
  },
  uploaded: {
    'en-us': 'Uploaded',
    'ru-ru': 'Загружено',
    'es-es': 'Subido',
    'fr-fr': 'Téléchargé',
    'uk-ua': 'Завантажено',
    'de-ch': 'Hochgeladen',
    'pt-br': 'Enviado',
    'hr-hr': 'Preneseno',
    nb: 'Opplastet',
  },
  remove: {
    'en-us': 'Remove',
    'ru-ru': 'Удалять',
    'es-es': 'Eliminar',
    'fr-fr': 'Retirer',
    'uk-ua': 'Видалити',
    'de-ch': 'Entfernen',
    'pt-br': 'Remover',
    'hr-hr': 'Ukloniti',
    nb: 'Fjerne',
  },
  search: {
    'en-us': 'Search',
    'ru-ru': 'Поиск',
    'es-es': 'Buscar',
    'fr-fr': 'Recherche',
    'uk-ua': 'Пошук',
    'de-ch': 'Suche',
    'pt-br': 'Procurar',
    'hr-hr': 'Pretraživanje',
    nb: 'Søk',
  },
  noResults: {
    'en-us': 'No Results',
    'ru-ru': 'Результаты отсутствуют',
    'es-es': 'No hay resultados',
    'fr-fr': 'Aucun résultat',
    'uk-ua': 'Немає результатів',
    'de-ch': 'Keine Resultate',
    'pt-br': 'Nenhum resultado',
    'hr-hr': 'Nema rezultata',
    nb: 'Ingen resultater',
  },
  notApplicable: {
    'en-us': 'N/A',
    'ru-ru': 'Н/Д',
    'es-es': 'N / A',
    'fr-fr': 'N/A',
    'uk-ua': 'Немає даних',
    'de-ch': 'N/A',
    'pt-br': 'N / D',
    'hr-hr': 'N/A',
    nb: 'Ikke aktuelt',
  },
  new: {
    'en-us': 'New',
    'ru-ru': 'Новый',
    'es-es': 'Nuevo',
    'fr-fr': 'Nouveau',
    'uk-ua': 'Новий',
    'de-ch': 'Neu',
    'pt-br': 'Novo',
    'hr-hr': 'Novi',
    nb: 'Ny',
  },
  edit: {
    'en-us': 'Edit',
    'ru-ru': 'Редактировать',
    'es-es': 'Editar',
    'fr-fr': 'Modifier',
    'uk-ua': 'Редагувати',
    'de-ch': 'Bearbeiten',
    'pt-br': 'Editar',
    'hr-hr': 'Uredi',
    nb: 'Redigere',
  },
  ignore: {
    'en-us': 'Ignore',
    'ru-ru': 'Игнорировать',
    'es-es': 'Ignorar',
    'fr-fr': 'Ignorer',
    'uk-ua': 'Ігнорувати',
    'de-ch': 'Ignorieren',
    'pt-br': 'Ignorar',
    'hr-hr': 'Zanemariti',
    nb: 'Overse',
  },
  proceed: {
    'en-us': 'Proceed',
    'ru-ru': 'Продолжить',
    'es-es': 'Proceder',
    'fr-fr': 'Procéder',
    'uk-ua': 'Продовжити',
    'de-ch': 'Fortfahren',
    'pt-br': 'Prosseguir',
    'hr-hr': 'Nastaviti',
    nb: 'Fortsette',
  },
  start: {
    comment: 'Noun',
    'en-us': 'Start',
    'ru-ru': 'Начинать',
    'es-es': 'Comenzar',
    'fr-fr': 'Commencer',
    'uk-ua': 'Початок',
    'de-ch': 'Start',
    'pt-br': 'Começar',
    'hr-hr': 'Start',
    nb: 'Start',
  },
  end: {
    comment: 'Noun',
    'en-us': 'End',
    'ru-ru': 'Конец',
    'es-es': 'Fin',
    'fr-fr': 'Fin',
    'uk-ua': 'Кінець',
    'de-ch': 'Ende',
    'pt-br': 'Fim',
    'hr-hr': 'Kraj',
    nb: 'Slutt',
  },
  update: {
    comment: 'Verb',
    'en-us': 'Update',
    'ru-ru': 'Обновлять',
    'es-es': 'Actualizar',
    'fr-fr': 'Mise à jour',
    'uk-ua': 'Оновлення',
    'de-ch': 'Aktualisieren',
    'pt-br': 'Atualizar',
    'hr-hr': 'Ažurirati',
    nb: 'Oppdatering',
  },
  fullDate: {
    'en-us': 'Full Date',
    'es-es': 'Fecha completa',
    'de-ch': 'Vollständiges Datum',
    'fr-fr': 'Date complète',
    'ru-ru': 'Полная дата',
    'uk-ua': 'Повна дата',
    'pt-br': 'Data completa',
    'hr-hr': 'Puni datum',
    nb: 'Full dato',
  },
  view: {
    comment: 'Verb',
    'en-us': 'View',
    'ru-ru': 'Вид',
    'es-es': 'Vista',
    'fr-fr': 'Voir',
    'uk-ua': 'Переглянути',
    'de-ch': 'Ansicht',
    'pt-br': 'Visualizar',
    'hr-hr': 'Pogled',
    nb: 'Utsikt',
  },
  opensInNewTab: {
    comment: 'Used in a hover-over message for links that open in new tab',
    'en-us': '(opens in a new tab)',
    'ru-ru': '(откроется в новой вкладке)',
    'es-es': '(Se abre en una pestaña nueva)',
    'uk-ua': '(відкриється в новій вкладці)',
    'de-ch': '(Öffnet sich in einer neuen Registerkarte)',
    'fr-fr': "(s'ouvre dans un nouvel onglet)",
    'pt-br': '(abre em uma nova aba)',
    'hr-hr': '(otvara se u novoj kartici)',
    nb: '(åpnes i en ny fane)',
  },
  openInNewTab: {
    comment: 'Used in a button that opens a link in a new tab',
    'en-us': 'Open in New Tab',
    'ru-ru': 'Открыть в новой вкладке',
    'es-es': 'Abrir en una pestaña nueva',
    'fr-fr': 'Ouvrir dans un nouvel onglet',
    'uk-ua': 'Відкрити в новій вкладці',
    'de-ch': 'Öffnet sich in einer neuen Registerkarte',
    'pt-br': 'Abrir em nova aba',
    'hr-hr': 'Otvori u novoj kartici',
    nb: 'Åpne i ny fane',
  },
  goToHomepage: {
    'en-us': 'Go to Home Page',
    'ru-ru': 'Перейти на главную страницу',
    'es-es': 'Ir a la página de inicio',
    'fr-fr': "Accéder à la page d'accueil",
    'uk-ua': 'Перейти на головну сторінку',
    'de-ch': 'Zur Startseite gehen',
    'pt-br': 'Ir para a página inicial',
    'hr-hr': 'Idi na početnu stranicu',
    nb: 'Gå til hjemmesiden',
  },
  actions: {
    'en-us': 'Actions',
    'ru-ru': 'Действия',
    'es-es': 'Comportamiento',
    'fr-fr': 'Actions',
    'uk-ua': 'Дії',
    'de-ch': 'Aktionen',
    'pt-br': 'Ações',
    'hr-hr': 'Radnje',
    nb: 'Handlinger',
  },
  chooseCollection: {
    'en-us': 'Choose Collection',
    'ru-ru': 'Выберите коллекцию',
    'es-es': 'Seleccione la colección',
    'fr-fr': 'Choisir une collection',
    'uk-ua': 'Виберіть колекцію',
    'de-ch': 'Sammlung auswählen',
    'pt-br': 'Selecione a coleção',
    'hr-hr': 'Odaberite kolekciju',
    nb: 'Velg samling',
  },
  ascending: {
    comment: 'As in "Ascending sort"',
    'en-us': 'Ascending',
    'ru-ru': 'Восходящий',
    'es-es': 'Ascendente',
    'fr-fr': 'Ascendant',
    'uk-ua': 'Зростаючий',
    'de-ch': 'Aufsteigend',
    'pt-br': 'Ascendente',
    'hr-hr': 'Uzlazno',
    nb: 'Stigende',
  },
  descending: {
    comment: 'As in "Descending sort"',
    'en-us': 'Descending',
    'ru-ru': 'Спуск',
    'es-es': 'Descendiendo',
    'fr-fr': 'Descendant',
    'uk-ua': 'Спадання',
    'de-ch': 'Absteigend',
    'pt-br': 'Descendente',
    'hr-hr': 'Silazni',
    nb: 'Synkende',
  },
  recordSets: {
    'en-us': 'Record Sets',
    'ru-ru': 'Наборы пластинок',
    'es-es': 'Conjuntos de récords',
    'fr-fr': "Ensemble d'enregistrements",
    'uk-ua': 'Набори записів',
    'de-ch': 'Satzgruppen',
    'pt-br': 'Conjuntos de discos',
    'hr-hr': 'Skupovi zapisa',
    nb: 'Rekordsett',
  },
  recordCount: {
    'en-us': 'Record Count',
    'ru-ru': 'Количество записей',
    'es-es': 'Recuento récord',
    'fr-fr': "Nombre d'enregistrements",
    'uk-ua': 'Кількість записів',
    'de-ch': 'Anzahl der Datensätze',
    'pt-br': 'Contagem de recordes',
    'hr-hr': 'Broj zapisa',
    nb: 'Antall poster',
  },
  size: {
    'en-us': 'Size',
    'ru-ru': 'Размер',
    'es-es': 'Tamaño',
    'fr-fr': 'Taille',
    'uk-ua': 'Розмір',
    'de-ch': 'Grösse',
    'pt-br': 'Tamanho',
    'hr-hr': 'Veličina',
    nb: 'Størrelse',
  },
  running: {
    'en-us': 'Running…',
    'ru-ru': 'Бег…',
    'es-es': 'Correr…',
    'fr-fr': "En cours d'exécution…",
    'uk-ua': 'Біг…',
    'de-ch': 'In Arbeit …',
    'pt-br': 'Correndo…',
    'hr-hr': 'Trčanje…',
    nb: 'Løping…',
  },
  noMatches: {
    'en-us': 'No Matches',
    'ru-ru': 'Совпадений нет',
    'es-es': 'No se encontraron coincidencias',
    'fr-fr': 'Aucune correspondance',
    'uk-ua': 'Немає збігів',
    'de-ch': 'Keine Treffer',
    'pt-br': 'Nenhum resultado encontrado',
    'hr-hr': 'Nema podudaranja',
    nb: 'Ingen treff',
  },
  searchQuery: {
    'en-us': 'Search Query',
    'ru-ru': 'Поисковый запрос',
    'es-es': 'Consulta de búsqueda',
    'fr-fr': 'Requête de recherche',
    'uk-ua': 'Пошуковий запит',
    'de-ch': 'Suchabfrage',
    'pt-br': 'Consulta de pesquisa',
    'hr-hr': 'Upit za pretraživanje',
    nb: 'Søkeforespørsel',
  },
  unknown: {
    'en-us': 'Unknown',
    'ru-ru': 'Неизвестный',
    'es-es': 'Desconocido',
    'fr-fr': 'Inconnu',
    'uk-ua': 'Невідомо',
    'de-ch': 'Unbekannt',
    'pt-br': 'Desconhecido',
    'hr-hr': 'Nepoznato',
    nb: 'Ukjent',
  },
  language: {
    'en-us': 'Language',
    'ru-ru': 'Язык',
    'es-es': 'Idioma',
    'fr-fr': 'Langue',
    'uk-ua': 'Мова',
    'de-ch': 'Sprache',
    'pt-br': 'Linguagem',
    'hr-hr': 'Jezik',
    nb: 'Språk',
  },
  country: {
    'en-us': 'Country',
    'ru-ru': 'Страна',
    'es-es': 'País',
    'fr-fr': 'Pays',
    'uk-ua': 'Країна',
    'de-ch': 'Land',
    'pt-br': 'País',
    'hr-hr': 'Zemlja',
    nb: 'Land',
  },
  viewRecord: {
    'en-us': 'View Record',
    'ru-ru': 'Просмотреть запись',
    'es-es': 'Ver registro',
    'fr-fr': "Afficher l'enregistrement",
    'uk-ua': 'Переглянути запис',
    'de-ch': 'Datensatz anzeigen',
    'pt-br': 'Ver registro',
    'hr-hr': 'Prikaži zapis',
    nb: 'Vis oppføring',
  },
  nullInline: {
    'en-us': '(null)',
    'ru-ru': '(нулевой)',
    'es-es': '(nulo)',
    'fr-fr': '(nul)',
    'uk-ua': '(нуль)',
    'de-ch': '(null)',
    'pt-br': '(nulo)',
    'hr-hr': '(null)',
    nb: '(null)',
  },
  filePickerMessage: {
    comment: 'Generic. Could refer to any file',
    'en-us': 'Choose a file or drag it here',
    'ru-ru': 'Выберите файл или перетащите его сюда.',
    'es-es': 'Seleccione un archivo o arrástrelo aquí.',
    'fr-fr': 'Choisissez un fichier ou faites-le glisser ici',
    'uk-ua': 'Виберіть файл або перетягніть його сюди',
    'de-ch': 'Wählen eine Datei oder ziehen sie hierhin',
    'pt-br': 'Selecione um arquivo ou arraste-o para cá.',
    'hr-hr': 'Odaberite datoteku ili je povucite ovdje',
    nb: 'Velg en fil eller dra den hit',
  },
  selectedFileName: {
    'en-us': 'Selected file',
    'ru-ru': 'Выбранный файл',
    'es-es': 'Archivo seleccionado',
    'fr-fr': 'Fichier sélectionné',
    'uk-ua': 'Вибраний файл',
    'de-ch': 'Gewählte Datei',
    'pt-br': 'Arquivo selecionado',
    'hr-hr': 'Odabrana datoteka',
    nb: 'Valgt fil',
  },
  all: {
    'en-us': 'All',
    'ru-ru': 'Все',
    'es-es': 'Todo',
    'fr-fr': 'Tous',
    'uk-ua': 'Усі',
    'de-ch': 'Alle',
    'pt-br': 'Todos',
    'hr-hr': 'Sve',
    nb: 'Alle',
  },
  unused: {
    'en-us': 'Unused',
    'ru-ru': 'Неиспользованный',
    'es-es': 'No usado',
    'fr-fr': 'Inutilisé',
    'uk-ua': 'Невикористаний',
    'de-ch': 'Unbenutzt',
    'pt-br': 'Não utilizado',
    'hr-hr': 'Nekorišteno',
    nb: 'Ubrukt',
  },
  ordinal: {
    'en-us': 'Ordinal',
    'ru-ru': 'Порядковый',
    'es-es': 'Ordinal',
    'fr-fr': 'Ordinal',
    'uk-ua': 'Порядковий',
    'de-ch': 'Reihenfolge',
    'pt-br': 'Ordinal',
    'hr-hr': 'Redni',
    nb: 'Ordinal',
  },
  export: {
    'en-us': 'Export',
    'ru-ru': 'Экспорт',
    'es-es': 'Exportar',
    'fr-fr': 'Exporter',
    'uk-ua': 'Експорт',
    'de-ch': 'Export',
    'pt-br': 'Exportar',
    'hr-hr': 'Izvoz',
    nb: 'Eksport',
  },
  import: {
    'en-us': 'Import',
    'ru-ru': 'Импорт',
    'es-es': 'Importar',
    'fr-fr': 'Importer',
    'uk-ua': 'Імпорт',
    'de-ch': 'Import',
    'pt-br': 'Importar',
    'hr-hr': 'Uvoz',
    nb: 'Import',
  },
  dismiss: {
    'en-us': 'Dismiss',
    'ru-ru': 'Увольнять',
    'es-es': 'Despedir',
    'fr-fr': 'Rejeter',
    'uk-ua': 'Відхилити',
    'de-ch': 'Ablehnen',
    'pt-br': 'Liberar',
    'hr-hr': 'Odbaciti',
    nb: 'Avskjedige',
  },
  id: {
    'en-us': 'ID',
    'ru-ru': 'ИДЕНТИФИКАТОР',
    'es-es': 'IDENTIFICACIÓN',
    'fr-fr': 'IDENTIFIANT',
    'uk-ua': 'Ідентифікатор',
    'de-ch': 'ID',
    'pt-br': 'EU IA',
    'hr-hr': 'ID',
    nb: 'ID',
  },
  filter: {
    'en-us': 'Filter',
    'ru-ru': 'Фильтр',
    'es-es': 'Filtrar',
    'fr-fr': 'Filtre',
    'uk-ua': 'Фільтр',
    'de-ch': 'Filter',
    'pt-br': 'Filtro',
    'hr-hr': 'Filter',
    nb: 'Filter',
  },
  results: {
    'en-us': 'Results',
    'ru-ru': 'Результаты',
    'es-es': 'Resultados',
    'fr-fr': 'Résultats',
    'uk-ua': 'Результати',
    'de-ch': 'Resultate',
    'pt-br': 'Resultados',
    'hr-hr': 'Rezultati',
    nb: 'Resultater',
  },
  downloadErrorMessage: {
    'en-us': 'Download Error Message',
    'ru-ru': 'Сообщение об ошибке загрузки',
    'es-es': 'Mensaje de error de descarga',
    'fr-fr': "Message d'erreur de téléchargement",
    'uk-ua': 'Повідомлення про помилку завантаження',
    'de-ch': 'Fehlermeldung herunterladen',
    'pt-br': 'Mensagem de erro de download',
    'hr-hr': 'Poruka o pogrešci preuzimanja',
    nb: 'Last ned feilmelding',
  },
  copied: {
    'en-us': 'Copied!',
    'ru-ru': 'Скопировано!',
    'es-es': '¡Copiado!',
    'fr-fr': 'Copié !',
    'uk-ua': 'Скопійовано!',
    'de-ch': 'Wurde kopiert!',
    'pt-br': 'Copiado!',
    'hr-hr': 'Kopirano!',
    nb: 'Kopiert!',
  },
  copyToClipboard: {
    'en-us': 'Copy to clipboard',
    'ru-ru': 'Скопировать в буфер обмена',
    'es-es': 'Copiar al portapapeles',
    'fr-fr': 'Copier dans le presse-papiers',
    'uk-ua': 'Копіювати в буфер обміну',
    'de-ch': 'In Zwischenablage kopieren',
    'pt-br': 'Copiar para a área de transferência',
    'hr-hr': 'Kopiraj u međuspremnik',
    nb: 'Kopier til utklippstavlen',
  },
  selected: {
    'en-us': 'Selected',
    'ru-ru': 'Избранные',
    'es-es': 'Seleccionado',
    'fr-fr': 'Selectionné',
    'uk-ua': 'Вибрано',
    'de-ch': 'Ausgewählt',
    'pt-br': 'Selecionado',
    'hr-hr': 'Odabrano',
    nb: 'Valgt',
  },
  expand: {
    'en-us': 'Expand',
    'ru-ru': 'Расширять',
    'es-es': 'Expandir',
    'fr-fr': 'Développer',
    'uk-ua': 'Розгорнути',
    'de-ch': 'Aufklappen',
    'pt-br': 'Expandir',
    'hr-hr': 'Proširiti',
    nb: 'Utvide',
  },
  expandAll: {
    'en-us': 'Expand All',
    'ru-ru': 'Развернуть все',
    'es-es': 'Expandir todo',
    'fr-fr': 'Développer tout',
    'uk-ua': 'Розгорнути все',
    'de-ch': 'Alle aufklappen',
    'pt-br': 'Expandir tudo',
    'hr-hr': 'Proširi sve',
    nb: 'Utvid alle',
  },
  collapse: {
    'en-us': 'Collapse',
    'es-es': 'Colapsar',
    'fr-fr': 'Réduire',
    'ru-ru': 'Крах',
    'uk-ua': 'Згорнути',
    'de-ch': 'Zuklappen',
    'pt-br': 'Colapso',
    'hr-hr': 'Kolaps',
    nb: 'Kollapse',
  },
  collapseAll: {
    'en-us': 'Collapse All',
    'ru-ru': 'Свернуть все',
    'es-es': 'Contraer todo',
    'fr-fr': 'Tout réduire',
    'uk-ua': 'Згорнути все',
    'de-ch': 'Alle zuklappen',
    'pt-br': 'Recolher tudo',
    'hr-hr': 'Sažmi sve',
    nb: 'Skjul alle',
  },
  reset: {
    'en-us': 'Reset',
    'ru-ru': 'Перезагрузить',
    'es-es': 'Reiniciar',
    'fr-fr': 'Réinitialiser',
    'uk-ua': 'Скинути',
    'de-ch': 'Zurücksetzen',
    'pt-br': 'Reiniciar',
    'hr-hr': 'Resetiraj',
    nb: 'Tilbakestill',
  },
  select: {
    'en-us': 'Select',
    'ru-ru': 'Выбирать',
    'es-es': 'Seleccionar',
    'fr-fr': 'Sélectionner',
    'uk-ua': 'Виберіть',
    'de-ch': 'Auswählen',
    'pt-br': 'Selecione',
    'hr-hr': 'Odaberite',
    nb: 'Velge',
  },
  none: {
    'en-us': 'None',
    'ru-ru': 'Никто',
    'es-es': 'Ninguno',
    'fr-fr': 'Aucun',
    'uk-ua': 'Жоден',
    'de-ch': 'Keine',
    'pt-br': 'Nenhum',
    'hr-hr': 'Ništa',
    nb: 'Ingen',
  },
  noneAvailable: {
    'en-us': 'None available',
    'ru-ru': 'Нет в наличии',
    'es-es': 'No hay ninguno disponible',
    'fr-fr': 'Aucun disponible',
    'uk-ua': 'Немає доступних',
    'de-ch': 'Keine Verfügbar',
    'pt-br': 'Nenhuma disponível',
    'hr-hr': 'Nema dostupnih',
    nb: 'Ingen tilgjengelige',
  },
  countLine: {
    comment: 'Example usage: Record Sets (1,234)',
    'en-us': '{resource:string} ({count:number|formatted})',
    'ru-ru': '{resource:string} ({count:number|formatted})',
    'es-es': '{resource:string} ({count:number|formatted})',
    'fr-fr': '{resource:string} ({count:number|formatted})',
    'uk-ua': '{resource:string} ({count:number|formatted})',
    'de-ch': '{resource:string} ({count:number|formatted})',
    'pt-br': '{resource:string} ({count:number|formatted})',
    'hr-hr': '{resource:string} ({count:number|formatted})',
    nb: '{resource:string} ({count:number|formatted})',
  },
  countLineOrMore: {
    comment: 'Example usage: Record Sets (1,234+)',
    'en-us': '{resource:string} ({count:number|formatted}+)',
    'de-ch': '{resource:string} ({count:number|formatted}+)',
    'es-es': '{resource:string} ({count:number|formatted}+)',
    'fr-fr': '{resource:string} ({count:number|formatted}+)',
    'pt-br': '{resource:string} ({count:number|formatted}+)',
    'ru-ru': '{resource:string} ({count:number|formatted}+)',
    'uk-ua': '{resource:string} ({count:number|formatted}+)',
    'hr-hr': '{resource:string} ({count:number|formatted}+)',
    nb: '{resource:string} ({count:number|formatted}+)',
  },
  jsxCountLine: {
    comment: 'Example usage: Record Sets (1,234)',
    'en-us': '{resource:string} <wrap>({count:number|formatted})</wrap>',
    'ru-ru': '{resource:string} <wrap>({count:number|formatted})</wrap>',
    'es-es': '{resource:string} <wrap>({count:number|formatted})</wrap>',
    'fr-fr': '{resource:string} <wrap>({count:number|formatted})</wrap>',
    'uk-ua': '{resource:string} <wrap>({count:number|formatted})</wrap>',
    'de-ch': '{resource:string} <wrap>({count:number|formatted})</wrap>',
    'pt-br': '{resource:string} <wrap>({count:number|formatted})</wrap>',
    'hr-hr': '{resource:string} <wrap>({count:number|formatted})</wrap>',
    nb: '{resource:string} <wrap> ({count:number|formatted}) </wrap>',
  },
  colonHeader: {
    comment: `
      Example usage: "Choose collection:". Used only if there is nothing else on
      this line after the colon heading
    `,
    'en-us': '{header:string}:',
    'ru-ru': '{header:string}:',
    'es-es': '{header:string}:',
    'fr-fr': '{header:string} :',
    'uk-ua': '{header:string}:',
    'de-ch': '{header:string}:',
    'pt-br': '{header:string}:',
    'hr-hr': '{header:string}:',
    nb: '{header:string}:',
  },
  colonLine: {
    comment: `
      Example usage: "Created by: Full Name" OR "Record Set: Record Set Name"
    `,
    'en-us': '{label:string}: {value:string}',
    'ru-ru': '{label:string}: {value:string}',
    'es-es': '{label:string}: {value:string}',
    'fr-fr': '{label:string} : {value:string}',
    'uk-ua': '{label:string}: {value:string}',
    'de-ch': '{label:string}: {value:string}',
    'pt-br': '{label:string}: {value:string}',
    'hr-hr': '{label:string}: {value:string}',
    nb: '{label:string}: {value:string}',
  },
  jsxColonLine: {
    comment: `
      Example usage: "Created by: Full Name" OR "Record Set: Record Set Name"
    `,
    'en-us': '{label:string}: <wrap />',
    'ru-ru': '{label:string}: <wrap />',
    'es-es': '{label:string}: <wrap />',
    'fr-fr': '{label:string} : <wrap />',
    'uk-ua': '{label:string}: <wrap />',
    'de-ch': '{label:string}: <wrap />',
    'pt-br': '{label:string}: <wrap />',
    'hr-hr': '{label:string}: <wrap />',
    nb: '{label:string}: <wrap />',
  },
  bulkSelect: {
    'en-us': 'Bulk Select',
    'es-es': 'Selección masiva',
    'de-ch': 'Mehrfachauswahl',
    'fr-fr': 'Sélection en masse',
    'ru-ru': 'Выбор группы',
    'uk-ua': 'Масовий вибір',
    'pt-br': 'Seleção em massa',
    'hr-hr': 'Skupni odabir',
    nb: 'Masseutvalg',
  },
  bulkReturn: {
    'en-us': 'Bulk Return',
    'de-ch': 'Massenrückgabe',
    'es-es': 'Devolución masiva',
    'fr-fr': 'Retour en masse',
    'ru-ru': 'Возврат большого количества товаров',
    'uk-ua': 'Масове повернення',
    'pt-br': 'Devolução em massa',
    'hr-hr': 'Povrat robe u skupnom stanju',
    nb: 'Masseretur',
  },
  bulkResolve: {
    'en-us': 'Bulk Resolve',
    'de-ch': 'Massenauflösung',
    'es-es': 'Resolución masiva',
    'fr-fr': 'Résolution en vrac',
    'ru-ru': 'Массовое разрешение',
    'uk-ua': 'Масове вирішення',
    'pt-br': 'Resolução em massa',
    'hr-hr': 'Skupno rješavanje',
    nb: 'Masseløsning',
  },
  timeRemaining: {
    'en-us': 'Time remaining',
    'es-es': 'Tiempo restante',
    'fr-fr': 'Temps restant',
    'ru-ru': 'Осталось времени',
    'uk-ua': 'Час, що залишився',
    'de-ch': 'Noch verbleibende Zeit',
    'pt-br': 'Tempo restante',
    'hr-hr': 'Preostalo vrijeme',
    nb: 'Gjenværende tid',
  },
  unlimited: {
    'en-us': 'Unlimited',
    'de-ch': 'Unbegrenzt',
    'es-es': 'Ilimitado',
    'fr-fr': 'Illimité',
    'ru-ru': 'Без ограничений',
    'uk-ua': 'Безлімітний',
    'pt-br': 'Ilimitado',
    'hr-hr': 'Neograničen',
    nb: 'Ubegrenset',
  },
  change: {
    comment: 'Verb',
    'en-us': 'Change',
    'de-ch': 'Ändern',
    'es-es': 'Cambiar',
    'fr-fr': 'Changement',
    'ru-ru': 'Изменять',
    'uk-ua': 'Зміна',
    'pt-br': 'Mudar',
    'hr-hr': 'Promijeniti',
    nb: 'Endre',
  },
  dontShowAgain: {
    'en-us': "Don't show this again",
    'es-es': 'No vuelvas a mostrar esto',
    'fr-fr': 'Ne plus afficher ceci',
    'ru-ru': 'Больше не показывайте это',
    'uk-ua': 'Не показувати це більше',
    'de-ch': 'Zeig das nicht noch einmal',
    'pt-br': 'Não mostrar isso novamente',
    'hr-hr': 'Ne prikazuj ovo više',
    nb: 'Ikke vis dette igjen',
  },
  multipleFilePickerMessage: {
    'en-us': 'Choose files or drag them here',
    'de-ch': 'Dateien auswählen oder hierher ziehen',
    'es-es': 'Seleccione los archivos o arrástrelos aquí.',
    'fr-fr': 'Choisissez des fichiers ou faites-les glisser ici',
    'ru-ru': 'Выберите файлы или перетащите их сюда.',
    'uk-ua': 'Виберіть файли або перетягніть їх сюди',
    'pt-br': 'Selecione os arquivos ou arraste-os para cá.',
    'hr-hr': 'Odaberite datoteke ili ih povucite ovdje',
    nb: 'Velg filer eller dra dem hit',
  },
  zoom: {
    'en-us': 'Zoom In',
    'fr-fr': 'Zoomer',
    'de-ch': 'Vergrößern',
    'es-es': 'Dar un golpe de zoom',
    'pt-br': 'Ampliar',
    'ru-ru': 'Увеличить масштаб',
    'uk-ua': 'Збільшити масштаб',
    'hr-hr': 'Uvećaj',
    nb: 'Zoom inn',
  },
  unzoom: {
    'en-us': 'Zoom Out',
    'fr-fr': 'Dézoomer',
    'de-ch': 'Herauszoomen',
    'es-es': 'Aleja la imagen',
    'pt-br': 'Diminuir o zoom',
    'ru-ru': 'Отдалиться',
    'uk-ua': 'Зменшити масштаб',
    'hr-hr': 'Smanji',
    nb: 'Zoom ut',
  },
} as const);
